import { useState, useCallback } from "react";
import { useAuthenticatedFetch } from "./useAuthenticatedFetch";

export interface VelocitySettings {
  id: string;
  calculationMethod: "rolling_90" | "custom_months";
  selectedMonths: string[];
}

export function useVelocitySettings() {
  const authFetch = useAuthenticatedFetch();
  const [settings, setSettings] = useState<VelocitySettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch("/api/velocity/settings");
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      setSettings(json.data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  const saveSettings = useCallback(
    async (data: {
      calculationMethod: string;
      selectedMonths: string[];
    }): Promise<boolean> => {
      try {
        const res = await authFetch("/api/velocity/settings", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        setSettings(json.data);
        return true;
      } catch (err: any) {
        setError(err.message);
        return false;
      }
    },
    [authFetch]
  );

  const startBackfill = useCallback(async (): Promise<string | null> => {
    try {
      const res = await authFetch("/api/velocity/backfill", {
        method: "POST",
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      return json.data?.bulkOperationId || null;
    } catch (err: any) {
      setError(err.message);
      return null;
    }
  }, [authFetch]);

  const pollBackfill = useCallback(
    async (
      operationId: string
    ): Promise<{
      status: string;
      processed?: boolean;
      variantsProcessed?: number;
      monthsPopulated?: number;
    }> => {
      try {
        const res = await authFetch(`/api/velocity/backfill/${operationId}`);
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        return json.data;
      } catch (err: any) {
        setError(err.message);
        return { status: "FAILED" };
      }
    },
    [authFetch]
  );

  return {
    settings,
    loading,
    error,
    loadSettings,
    saveSettings,
    startBackfill,
    pollBackfill,
  };
}
